/**
 * Welle 119 — Rechnungsausgangsbuch (Lasche „ReAusgBuch", imho_v2.re_ausg_buch) als eigener Reiter:
 * Suche, Filter Nummernkreis / Jahr / Status; öffnen, PDF, duplizieren (alte Rechnung als Vorlage), stornieren, löschen.
 */
import React, { useCallback, useEffect, useState } from "react";
import { API, json, euro, deDatum } from "./hilfen";
import "../mietverwaltung/Tab5Bescheinigungen.css";
import "./Rechnungen.css";

export default function Ausgangsbuch({ onOeffnen, onDuplizieren, neuLaden = 0 }) {
  const [liste, setListe] = useState([]);
  const [q, setQ] = useState("");
  const [kreis, setKreis] = useState("");
  const [jahr, setJahr] = useState("");
  const [status, setStatus] = useState("");
  const [kreise, setKreise] = useState([]);
  const [meldung, setMeldung] = useState(null);
  const [busy, setBusy] = useState(false);

  const laden = useCallback(() => {
    const p = new URLSearchParams({ q, kreis, status, limit: 300 });
    if (jahr) p.set("jahr", jahr);
    fetch(`${API}/liste?${p}`).then(json).then((d) => setListe(d.rechnungen || [])).catch(() => setListe([]));
  }, [q, kreis, jahr, status]);
  useEffect(() => { const t = setTimeout(laden, 250); return () => clearTimeout(t); }, [laden, neuLaden]);
  useEffect(() => { fetch(`${API}/kreise`).then(json).then((d) => setKreise(d.kreise || [])).catch(() => {}); }, [neuLaden]);

  const pdf = async (row) => {
    setBusy(true);
    try {
      const d = await json(await fetch(`${API}/${row.id}`));
      const r = await fetch(`${API}/pdf?ablegen=0`, { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ daten: d }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "PDF fehlgeschlagen");
      const url = URL.createObjectURL(await r.blob());
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setMeldung({ ok: false, text: String(e.message || e) }); }
    setBusy(false);
  };
  const storno = async (row) => {
    if (!window.confirm(`${row.rg_nr} stornieren? Die Zeile bleibt im Rechnungsausgangsbuch (Status „storniert"), `
      + "die Nummer bleibt verbraucht, übernommene Arbeitszeit wird wieder frei.")) return;
    try { await json(await fetch(`${API}/${row.id}/storno`, { method: "POST" })); laden(); }
    catch (e) { setMeldung({ ok: false, text: String(e.message || e) }); }
  };
  const loeschen = async (row) => {
    if (!window.confirm(`Entwurf ${row.rg_nr || row.id} löschen?`)) return;
    try { await json(await fetch(`${API}/${row.id}`, { method: "DELETE" })); laden(); }
    catch (e) { setMeldung({ ok: false, text: String(e.message || e) }); }
  };

  const jetzt = new Date().getFullYear();
  const summe = liste.filter((r) => r.status !== "storniert" && r.status !== "entwurf")
    .reduce((a, r) => a + (r.brutto ?? r.netto ?? 0), 0);

  return (
    <div className="rg-wrap">
      <div className="mb-sektion">
        <div className="mb-sektion-titel">Rechnungsausgangsbuch (Lasche „ReAusgBuch“)</div>
        <div className="rg-buch-filter">
          <input className="mb-input" value={q} placeholder="suchen: Nummer, Name, Objekt, Inhalt …" onChange={(e) => setQ(e.target.value)} />
          <select className="mb-input" value={kreis} onChange={(e) => setKreis(e.target.value)}>
            <option value="">alle Nummernkreise</option>
            {kreise.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <select className="mb-input" value={jahr} onChange={(e) => setJahr(e.target.value)}>
            <option value="">alle Jahre</option>
            {[0, 1, 2, 3, 4, 5].map((i) => <option key={i} value={jetzt - i}>{jetzt - i}</option>)}
          </select>
          <select className="mb-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">alle Status</option>
            <option value="entwurf">Entwurf</option><option value="gestellt">gestellt</option>
            <option value="storniert">storniert</option><option value="excel">aus der Excel</option>
          </select>
          <span className="rg-klein" style={{ margin: 0 }}>{liste.length} Zeilen · gestellt {euro(summe)}</span>
        </div>
        {meldung && <div className={`mb-meldung ${meldung.ok ? "ok" : "fehler"}`}>{meldung.text}</div>}
        <div className="rg-tabelle-rahmen">
          <table className="rg-tabelle rg-buch">
            <thead><tr><th>Rg-Nr</th><th>Datum</th><th>Absender</th><th>Objekt</th><th>Debitor · Name</th><th>Inhalt</th><th className="r">Brutto</th><th>Status</th><th /></tr></thead>
            <tbody>
              {liste.map((r) => (
                <tr key={r.id} className={r.status === "storniert" ? "rg-storniert" : ""}>
                  <td>{r.rg_nr || (r.status === "entwurf" ? <i className="rg-klein">Entwurf</i> : "—")}</td><td>{deDatum(r.datum)}</td><td>{r.absender}</td><td>{r.objekt}</td>
                  <td>{[r.debitor, r.name].filter(Boolean).join(" · ")}</td><td className="rg-inhalt">{r.inhalt}</td>
                  <td className="r">{r.brutto != null ? euro(r.brutto) : (r.netto != null ? euro(r.netto) : "")}</td>
                  <td>{r.status || (r.formular ? "" : "Excel")}</td>
                  <td className="rg-aktionen">
                    {r.formular && <button className="mb-link" onClick={() => onOeffnen(r.id)}>öffnen</button>}
                    {r.formular && r.status !== "entwurf" && <button className="mb-link" disabled={busy} onClick={() => pdf(r)}>PDF</button>}
                    <button className="mb-link" title="Als Vorlage für eine neue Rechnung nehmen" onClick={() => onDuplizieren(r.id)}>duplizieren</button>
                    {r.status === "gestellt" && <button className="mb-link" onClick={() => storno(r)}>stornieren</button>}
                    {r.status === "entwurf" && <button className="mb-link" onClick={() => loeschen(r)}>löschen</button>}
                  </td>
                </tr>
              ))}
              {!liste.length && <tr><td colSpan={9} className="rg-klein">Keine Einträge.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
